// Shell abstraction — every UI shell (classic chat, atlas, workspace, dashboard)
// receives the SAME ShellProps and consumes the SAME state machine. Shells differ
// only in chrome and layout — never in data flow. The chat state machine and SSE
// pipeline live in useChatState() and are passed in here pre-wired.
import type { AssistantTurn, Turn } from '../types';

export type Shell = 'classic' | 'atlas' | 'workspace' | 'dashboard';

export const SHELL_VALUES: Shell[] = ['classic', 'atlas', 'workspace', 'dashboard'];

export interface CurrentTurn {
  query: string;
  assistant: AssistantTurn;
  toolsInFlight: string[];
  streaming: boolean;
  /** Latest backend-emitted "stage" label — preferred by the loading
   *  indicator over the time-based phased fallback. Empty until the
   *  first stage event arrives (or stays empty for old backends that
   *  don't emit them). */
  stageLabel?: string;
}

export interface ShellProps {
  /** The live/streaming turn, or null on blank state. */
  current: CurrentTurn | null;
  /** Completed turns (chat history). */
  priorTurns: Turn[];
  /** Fire a new query against /query_stream. */
  submit: (query: string) => void;
  /** Reset current → null, push the previous turn into priorTurns. */
  askAnother: () => void;
  /** Identity of the active shell (so shells can highlight their own switcher button if needed). */
  shell: Shell;
  /** Switch to a different shell. Persisted to localStorage by the parent. */
  onShellChange: (s: Shell) => void;
}

export function isShell(v: unknown): v is Shell {
  return typeof v === 'string' && (SHELL_VALUES as string[]).includes(v);
}
